import { ItemEditor } from "@campfirelearning/sdk-lti";
import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import uuid from "uuid";

import { gql } from "@examspark/api";
import {
  Button,
  ConfirmModal,
  ContainerButton,
  FlexBox,
  Loading,
  Modal,
  Spinner,
  Toolbar,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconPageNextLine,
  IconPagePreviousLine,
} from "@examspark/common-ui/dist/components/icons";
import { ResourceMaturity, ResourceVisibilityType } from "@examspark/types";

import { useQueryGetAppLicense } from "../../api/operations/app";
import {
  useQueryGetItem,
  useQuerySearchClonedItems,
} from "../../api/operations/items";
import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useAppContext } from "../../providers/AppContext";
import ItemPreview from "../items/ItemPreview";
import NoResults from "../NoResults";

const StyledNoResults = styled(NoResults)`
  height: 100%;
`;

export const useLtiParams = () => {
  const [bonfire] = useAppContext();

  const app = useQueryGetAppLicense(bonfire.app?.id, bonfire.workspace?.id);

  return useMemo(() => {
    if (!app.data?.appKey) {
      return null;
    }

    const params: Record<string, string> = {
      lti_version: "LTI-1p0",
      lti_message_type: "basic-lti-launch-request",
      resource_link_id: "0",
      user_id: bonfire.user?.email ?? "",
      // user_id: "student@example.com",
      lis_person_contact_email_primary: bonfire.user?.email ?? "",
      lis_person_name_given: bonfire.user?.firstName ?? "",
      lis_person_name_family: bonfire.user?.lastName ?? "",
      lis_person_name_full: `${bonfire.user?.firstName} ${bonfire.user?.lastName}`,
      roles: "Instructor",

      oauth_callback: "about:blank",
      oauth_signature_method: "HMAC-SHA1",
      oauth_consumer_key: app.data?.appKey ?? "",
      oauth_timestamp: String(Math.floor(Date.now() / 1000)),
      oauth_nonce: "123",
      oauth_version: "1.0",
    };

    return params;
  }, [
    app.data?.appKey,
    bonfire.user?.email,
    bonfire.user?.firstName,
    bonfire.user?.lastName,
  ]);
};

/**
 * @deprecated This needs to be swapped out of Bonfire as soon as it can be
 * replaced by the better ActivityEditor component. */
export const ReplaceActivityItem = (props: {
  itemToBeCloned: string;
  targetBank: string;
  isOpen: boolean;
  onRequestClose: (newItemId: string | null) => void;
}) => {
  const [view, setView] = useState<"existing" | "lti">("existing");

  const client = useQueryClient();

  const [itemId, setItemId] = useState<string | null>(null);

  const bankToCloneTo = props.targetBank;

  const ltiParams = useLtiParams();
  const authorizationContext = useAuthorizationContext();

  const pageSize = 25;
  const existingClones = useQuerySearchClonedItems({
    targetBankId: bankToCloneTo,
    originResourceId: [props.itemToBeCloned, "item#v0"].join("/"),
    limit: pageSize,
  });
  const itemPreviw = useQueryGetItem(itemId ?? undefined, "item#v0");
  const [page, setPage] = useState(0);

  // used by the loading spinner
  const [ltiStatus, setLtiStatus] = useState<"mounted" | "launched" | null>(
    null,
  );
  const [itemModified, setItemModified] = useState(false);

  const currentPage = existingClones.data?.pages[page];
  const totalPageCount = Math.ceil((currentPage?.total ?? 0) / pageSize);
  const consumer = useRef<ItemEditor | null>(null);

  const itemEditorDomId = useRef(uuid.v4());

  const cloneItem = async () => {
    try {
      const itemData = await gql.operations.item.getItemForClone(
        props.itemToBeCloned,
        authorizationContext,
      );

      if (!itemData) {
        window.alert("Item not found");
        return;
      }

      const newItemData = produce(itemData, (draft) => {
        // capture origin data
        if (itemData.originResourceId) {
          draft.originIdInc = itemData.originIdInc;
          draft.originResourceId = itemData.originResourceId;
          draft.originWorkspaceId = itemData.workspaceId;
          draft.originBankId = itemData.bankId;
        } else {
          draft.originIdInc = itemData.idInc;
          draft.originResourceId = [itemData.id, itemData.sk].join("/");
          draft.originWorkspaceId = itemData.workspaceId;
          draft.originBankId = itemData.bankId;
        }

        draft.id = uuid();

        draft.alignmentIds = itemData.alignmentIds ?? [];
        draft.creatorId = authorizationContext.userId;
        draft.contextIds = itemData.contextIds ?? [];
        draft.maturity = ResourceMaturity.Operational;
        draft.visibility = ResourceVisibilityType.Private;

        draft.bankId = bankToCloneTo;
        draft.workspaceId = authorizationContext.workspaceId;

        draft.name = (draft.name ?? "") + " (Teacher Copy)";
      });

      const newItemCreateResponse = await gql.operations.item.createItem(
        newItemData,
        authorizationContext,
      );

      if (!newItemCreateResponse) {
        window.alert("Item not created");
        return;
      }

      setItemId(newItemCreateResponse.id);

      return newItemCreateResponse.id;
    } catch (e) {
      console.error(e);
    }

    client.invalidateQueries(["cloned-items"]);
  };

  useEffect(() => {
    if (ltiParams == null) {
      console.info("Waiting for LTI params");
      return;
    }

    if (!itemId) {
      console.info("Waiting on item id");
      return;
    }

    if (view !== "lti") {
      console.info("Not in LTI view");
      return;
    }

    if (!consumer.current) {
      consumer.current = new ItemEditor({
        id: itemId,
        params: ltiParams,
        domain:
          process.env.REACT_APP_REST_API_URL ?? "https://api.examspark.com",
        signingFunction:
          (launchUrl: string) => async (params: Record<string, string>) => {
            try {
              const signer = await gql.operations.app.signCampfireLti(
                launchUrl,
                params,
                authorizationContext,
              );

              return {
                ...params,
                oauth_signature: signer.data?.signCampfireLti.signature ?? "",
              };
            } catch (e) {
              console.error(e);
            }

            return params;
          },
      });

      consumer.current.addEventListener("message", (event) => {
        if (event.data.name === "launched") {
          setLtiStatus("launched");
        } else if (event.data.name === "itemHasUnsavedChanges") {
          setItemModified(event.data.data.modified);
        }
      });

      consumer.current.mount(itemEditorDomId.current).then(() => {
        console.log("Mounted!");
        setLtiStatus("mounted");
      });
    }
  }, [authorizationContext, itemId, ltiParams, view]);

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={() => {
        if (!window.confirm("Are you sure you want to cancel?")) return;
        props.onRequestClose(itemId);
      }}
      flexBodyColumn
      size={"mobile"}
      title="Replace Activity Item"
    >
      {view === "existing" && (
        <Modal.PaddedBody style={{ display: "flex" }}>
          <StyledForm>
            {existingClones.isLoading && <Spinner />}
            {!!existingClones.error && <div>Error loading existing items</div>}
            <StyledItemPreviewContainer>
              <StyledItemPreviewList>
                <Toolbar>
                  <Button
                    title="New"
                    color="create"
                    onClick={async () => {
                      setView("lti");
                      await cloneItem();
                    }}
                    icon={<IconAddLine width={12} />}
                  >
                    Teacher Copy
                  </Button>
                  <Toolbar.Right>
                    <Button
                      title="Back"
                      onClick={() => {
                        setPage((page) => page - 1);
                      }}
                      disabled={page === 0}
                    >
                      <IconPagePreviousLine width={13} />
                    </Button>

                    <Button
                      title="Next"
                      onClick={async () => {
                        // If we don't have the next page yet, fetch it
                        if (
                          existingClones.data &&
                          existingClones.data.pages.length <= page + 1 &&
                          !existingClones.isFetchingNextPage
                        ) {
                          await existingClones.fetchNextPage();
                        }
                        // Increment the page itself once it's fetched
                        setPage((page) => page + 1);
                      }}
                      disabled={
                        existingClones.isFetchingNextPage ||
                        page + 1 >= totalPageCount
                      }
                    >
                      <IconPageNextLine width={12} />
                    </Button>
                  </Toolbar.Right>
                </Toolbar>
                {existingClones.isFetchingNextPage && <Spinner />}
                <StyledItemPreviewItems>
                  {currentPage?.items?.map((i) => (
                    <ItemCard
                      key={i.id}
                      selected={itemId === i.id}
                      onClick={() => {
                        setItemId(i.id);
                      }}
                    >
                      <div>{i.name}</div>
                      {i.createdAt && (
                        <div>
                          Created on{" "}
                          {new Date(i.createdAt).toLocaleDateString()}
                        </div>
                      )}
                    </ItemCard>
                  ))}
                </StyledItemPreviewItems>
              </StyledItemPreviewList>
              {!itemId && (
                <div>
                  <NoResults>Select an item to preview it</NoResults>
                </div>
              )}
              {itemPreviw.isLoading && itemId && <Spinner />}
              {!!itemPreviw.error && <div>Error loading item</div>}
              {itemPreviw.data && (
                <StyledItemPreview>
                  <ItemPreview data={itemPreviw.data} />
                </StyledItemPreview>
              )}
            </StyledItemPreviewContainer>
          </StyledForm>
        </Modal.PaddedBody>
      )}

      {view === "lti" && ltiStatus !== "launched" && (
        <StyledNoResults icon={<Loading />}>
          {ltiStatus == null ? "Mounting" : "Loading"}
        </StyledNoResults>
      )}

      <iframe
        title="Campfire SDK"
        style={{
          display:
            view === "lti" && ltiStatus === "launched" ? "block" : "none",
          border: "none",
        }}
        id={itemEditorDomId.current}
        name={itemEditorDomId.current}
        height="100%"
        width="100%"
      />

      <Modal.BasicFooter>
        <Button
          title="Back"
          color="secondary"
          onClick={() => {
            setItemId(null);
            props.onRequestClose(null);
          }}
        >
          Cancel
        </Button>
        {view === "existing" && itemId && (
          <Button
            title="Select and Edit"
            color="primary"
            onClick={() => setView("lti")}
          >
            Select and Edit
          </Button>
        )}
        {itemId && (
          <>
            <Button
              title="Continue"
              color="primary"
              onClick={async () => {
                if (view === "lti" && itemModified) {
                  const isConfirmed = await ConfirmModal.show({
                    children: (
                      <Modal.PaddedBody>
                        Please ensure you have saved your item before returning
                        to the activity creator.
                      </Modal.PaddedBody>
                    ),
                    title: "Return to Activity",
                    confirmLabel: "Yes, I saved",
                  });

                  if (!isConfirmed) return;
                }

                setItemId(null);
                props.onRequestClose(itemId);
              }}
            >
              {view === "lti" ? "Done" : "Select"}
            </Button>
          </>
        )}
      </Modal.BasicFooter>
    </Modal>
  );
};
export const ItemCard = styled(ContainerButton)<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 1rem 0.5rem;
  width: 100%;
  // height: 6rem;
  gap: 0.5rem;

  text-align: left;
  align-items: flex-start;
  border-bottom: 1px solid #ccc;

  ${(props) => props.selected && `background-color: #e4e3ec;`}
`;

const StyledItemPreviewContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;

  flex: 1;
`;

const StyledItemPreviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-inline: 0.5rem;

  border-right: 1px solid #ccc;
`;

const StyledItemPreviewItems = styled.div`
  height: 100%;
  width: 25rem;
  contain: size;
  overflow: auto;
`;

const StyledItemPreview = styled.div`
  flex: 1;

  contain: size;
`;

const StyledForm = styled(FlexBox)`
  flex: 1;
`;
